// ---------------------------------------------------------------------------
// PlanogramEditor
//
// The OOP core of the refactor. Wraps a PlanogramData snapshot and exposes
// every mutation that used to be a standalone handler inside
// PlanogramDetails.tsx (handleDeleteCell, handleClearCell, handleMergeRight,
// handleMergeLeft, handleMergeMultiple, handleUnmerge, handleMergeSelected,
// handleAddColumn, handleAddRow, handleSchemeSettingsSave, handleAddProduct,
// handleUpdateCellField, handleDuplicateCell, handleChangeDataSave) as a
// method on this class instead.
//
// Design:
//   - Immutable / copy-on-write: every mutator returns a NEW PlanogramEditor
//     wrapping a new PlanogramData. `this.data` is never mutated in place.
//     This means `usePlanogramEditor` (the React hook layer) can just be
//     `useState<PlanogramEditor>` + `setEditor(editor.mergeRight(...))` —
//     no manual deep-cloning or "did I forget to spread this nested object"
//     bugs, because the class itself guarantees a fresh object graph.
//   - No React, no hooks, no DOM. Fully unit-testable: construct an editor
//     from a fixture PlanogramData, call a method, assert on the returned
//     grid. None of PlanogramGridOps or this class know that React exists.
//   - Delegates grid topology (merge/clear/resize) to PlanogramGridOps and
//     product-cell shaping to PlanogramProductCellFactory — this class's own
//     job is just "hold the PlanogramData, dispatch to the right pure
//     function, wrap the result back into a new PlanogramData".
//   - Validation / no-op mutations return `this` unchanged (same reference)
//     rather than throwing, mirroring the original handlers' early-returns
//     (e.g. "Cannot merge right: already in last column" just `console.warn`'d
//     and did nothing). Callers can cheaply check `next === prev` if they
//     need to know whether anything actually changed.
// ---------------------------------------------------------------------------

import { PlanogramData, PlanogramCellData, ProductOption, CellKey, parseCellKey } from './types';
import { PlanogramGridOps } from './PlanogramGridOps';
import { PlanogramProductCellFactory } from './PlanogramProductCellFactory';
import { createEmptyCell } from '../../model/PlanogramCellModel';

export type MergeSelectedResult = { editor: PlanogramEditor; error?: 'invalidSelection' };

export class PlanogramEditor {
    private constructor(public readonly data: PlanogramData) {}

    // -----------------------------------------------------------------------
    // Construction
    // -----------------------------------------------------------------------

    static from(data: PlanogramData): PlanogramEditor {
        return new PlanogramEditor(data);
    }

    static createEmpty(rows: number, columns: number, name: string, id: string): PlanogramEditor {
        const cells: PlanogramCellData[][] = [];
        for (let i = 0; i < rows; i++) {
            const row: PlanogramCellData[] = [];
            for (let j = 0; j < columns; j++) {
                row.push(createEmptyCell({ rowIndex: i, colIndex: j }) as unknown as PlanogramCellData);
            }
            cells.push(row);
        }

        return new PlanogramEditor({
            id,
            name,
            rows,
            columns,
            cells,
            status: 'DRAFT',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });
    }

    /** Returns a new editor wrapping a deep-cloned snapshot — used for the "origin" baseline in the unsaved-changes guard. */
    snapshot(): PlanogramEditor {
        return new PlanogramEditor(JSON.parse(JSON.stringify(this.data)));
    }

    private withGrid(grid: PlanogramData['cells']): PlanogramEditor {
        return new PlanogramEditor({ ...this.data, cells: grid });
    }

    private withData(patch: Partial<PlanogramData>): PlanogramEditor {
        return new PlanogramEditor({ ...this.data, ...patch });
    }

    // -----------------------------------------------------------------------
    // Cell-level mutations
    // -----------------------------------------------------------------------

    updateCellField(
        rowIndex: number,
        colIndex: number,
        field: string,
        value: unknown,
    ): PlanogramEditor {
        return this.withGrid(
            PlanogramGridOps.updateCellField(
                this.data.cells,
                rowIndex,
                colIndex,
                field,
                value,
            ),
        );
    }

    deleteCell(rowIndex: number, colIndex: number): PlanogramEditor {
        return this.withGrid(PlanogramGridOps.deleteCell(this.data.cells, rowIndex, colIndex));
    }

    clearCell(rowIndex: number, colIndex: number): PlanogramEditor {
        return this.withGrid(PlanogramGridOps.clearCell(this.data.cells, rowIndex, colIndex));
    }

    clearCells(keys: CellKey[]): PlanogramEditor {
        const coords = keys.map(parseCellKey);
        return this.withGrid(PlanogramGridOps.clearCells(this.data.cells, coords, this.data.columns));
    }

    duplicateCell(sourceRowIndex: number, sourceColIndex: number, targetRowIndex: number, targetColIndex: number): PlanogramEditor {
        return this.withGrid(
            PlanogramGridOps.duplicateCell(this.data.cells, sourceRowIndex, sourceColIndex, targetRowIndex, targetColIndex),
        );
    }

    // -----------------------------------------------------------------------
    // Product assignment
    // -----------------------------------------------------------------------

    /** Optimistic write — full product cell including name. See PlanogramProductCellFactory for why this is split from patchProductDetails. */
    addProduct(rowIndex: number, colIndex: number, productOption: ProductOption): PlanogramEditor {
        if (!this.data.cells?.[rowIndex]?.[colIndex]) return this;

        const grid = this.data.cells.map(row => [...row]);
        grid[rowIndex][colIndex] = PlanogramProductCellFactory.build(grid[rowIndex][colIndex], productOption);
        return this.withGrid(grid);
    }

    /** Patches price/qty/currency only, once the detail-fetch resolves. Never overwrites name. */
    patchProductDetails(rowIndex: number, colIndex: number, fullProduct: ProductOption): PlanogramEditor {
        if (!this.data.cells?.[rowIndex]?.[colIndex]) return this;

        const grid = this.data.cells.map(row => [...row]);
        grid[rowIndex][colIndex] = PlanogramProductCellFactory.patchDetails(grid[rowIndex][colIndex], fullProduct);
        return this.withGrid(grid);
    }

    applyBulkCellSettings(keys: CellKey[], buildCell: (rowIndex: number, colIndex: number, prevCell: PlanogramCellData) => PlanogramCellData): PlanogramEditor {
        const grid = this.data.cells.map(row => [...row]);
        keys.forEach(key => {
            const { rowIndex, colIndex } = parseCellKey(key);
            grid[rowIndex][colIndex] = buildCell(rowIndex, colIndex, grid[rowIndex][colIndex]);
        });
        return this.withGrid(grid);
    }

    applySingleCellSettings(rowIndex: number, colIndex: number, buildCell: (prevCell: PlanogramCellData) => PlanogramCellData): PlanogramEditor {
        const grid = this.data.cells.map(row => [...row]);
        grid[rowIndex][colIndex] = buildCell(grid[rowIndex][colIndex]);
        return this.withGrid(grid);
    }

    applyPriceChangeToSelection(
        keys: CellKey[],
        formData: {
            price: unknown;
            effectiveDate: unknown;
        },
    ): PlanogramEditor {
        const grid =
            this.data.cells.map(row => [...row]);

        const effectiveDate:
            | string
            | Date
            | null =
            formData.effectiveDate instanceof Date ||
            typeof formData.effectiveDate === 'string'
                ? formData.effectiveDate
                : null;

        keys.forEach(key => {
            const {
                rowIndex,
                colIndex,
            } = parseCellKey(key);

            const cell =
                grid[rowIndex][colIndex];

            if (
                cell.type !== 'product' ||
                !cell.product
            ) {
                return;
            }

            grid[rowIndex][colIndex] = {
                ...cell,
                price:
                    formData.price,
                effectiveDate,
                product: {
                    ...cell.product,
                    price:
                        formData.price,
                    currency:
                        cell.currency,
                    effectiveDate,
                },
            };
        });

        return this.withGrid(grid);
    }

    // -----------------------------------------------------------------------
    // Merge / unmerge
    // -----------------------------------------------------------------------

    mergeRight(rowIndex: number, colIndex: number): PlanogramEditor {
        const grid = PlanogramGridOps.mergeRight(this.data.cells, rowIndex, colIndex, this.data.columns);
        return grid ? this.withGrid(grid) : this;
    }

    mergeLeft(rowIndex: number, colIndex: number): PlanogramEditor {
        const grid = PlanogramGridOps.mergeLeft(this.data.cells, rowIndex, colIndex, this.data.columns);
        return grid ? this.withGrid(grid) : this;
    }

    mergeSelected(selectionKeys: CellKey[]): MergeSelectedResult {
        const result = PlanogramGridOps.mergeSelected(this.data.cells, selectionKeys);
        if (!result) return { editor: this };
        if (result.error) return { editor: this, error: result.error };
        return { editor: this.withGrid(result.grid) };
    }

    unmerge(rowIndex: number, colIndex: number): PlanogramEditor {
        const grid = PlanogramGridOps.unmerge(this.data.cells, rowIndex, colIndex, this.data.columns);
        return grid ? this.withGrid(grid) : this;
    }

    isRowFullyMerged(rowIndex: number): boolean {
        return PlanogramGridOps.isRowFullyMerged(this.data.cells, rowIndex, this.data.columns);
    }

    // -----------------------------------------------------------------------
    // Resize
    // -----------------------------------------------------------------------

    addColumn(maxColumns = 20): { editor: PlanogramEditor; error?: 'columnsRange' } {
        if (this.data.columns >= maxColumns) return { editor: this, error: 'columnsRange' };
        const grid = PlanogramGridOps.addColumn(this.data.cells, this.data.columns);
        return { editor: this.withData({ columns: this.data.columns + 1, cells: grid }) };
    }

    addRow(maxRows = 20): { editor: PlanogramEditor; error?: 'rowsRange' } {
        if (this.data.rows >= maxRows) return { editor: this, error: 'rowsRange' };
        const grid = PlanogramGridOps.addRow(this.data.cells, this.data.rows, this.data.columns);
        return { editor: this.withData({ rows: this.data.rows + 1, cells: grid }) };
    }

    resize(newRows: number, newColumns: number): PlanogramEditor {
        const grid = PlanogramGridOps.resize(this.data.cells, this.data.rows, this.data.columns, newRows, newColumns);
        return this.withData({ rows: newRows, columns: newColumns, cells: grid });
    }

    // -----------------------------------------------------------------------
    // Metadata
    // -----------------------------------------------------------------------

    rename(name: string, externalSystemId: string): PlanogramEditor {
        return this.withData({ name, externalSystemId });
    }

    hasTiedDevices(): boolean {
        return !!this.data.tiedDevicesCount && this.data.tiedDevicesCount > 0;
    }

    isComplete(checkComplete: (data: PlanogramData) => boolean): boolean {
        return checkComplete(this.data);
    }

    hasAnyProductCell(): boolean {
        return this.data.cells?.some(row => row.some(cell => cell?.type === 'product' && !!cell.product)) ?? false;
    }
}